/**
 * CommentQueryParamsListener — Unified component replacing:
 *   - InlineCommentQueryParamsListener
 *   - ObjectCommentQueryParamsListener
 *
 * Executes a single query per commentId and routes to type-specific
 * handlers based on the comment's location.type (INLINE vs FOOTER/GENERAL).
 *
 * Consumer wiring (NOT done here — see doc):
 *   https://hello.atlassian.net/wiki/spaces/~7012168ccbf0591f8403491bd079b0826221f/pages/6163933063
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, FormattedMessage } from 'react-intl';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import {
	CommentType,
	type CommentData,
	useCommentsDataState,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type {
	Comment,
	CommentLocation,
	CommentReply,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	buildDefaultCommentsContentStatusFilters,
	findRootCommentForReply,
} from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import {
	handleDeepLinkNavigation,
	handleFooterCommentsNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import {
	scrollToElementWithViewingRoom,
	checkElementExpand,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { getLogger } from '@confluence/logger';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { usePageContentId } from '@confluence/page-context';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { createReplyToCommentParams, type AuthorWithAccountId } from './replyToCommentHelper.ts';
import { useRetryOnMutation } from './useRetryOnMutation.ts';

// Local types for CommentQueryParamsListenerQuery until Relay artifacts exist for this query.
type LegacyCommentQueryLocation = {
	type?: string | null;
	inlineMarkerRef?: string | null;
	inlineResolveProperties?: { resolved?: boolean } | null;
	commentResolveProperties?: { resolved?: boolean } | null;
};

type LegacyCommentQueryNode = {
	id?: string;
	parentId?: string | null;
	location?: LegacyCommentQueryLocation | null;
	ancestors?: ReadonlyArray<{
		location?: LegacyCommentQueryLocation | null;
	}> | null;
};

type LegacyCommentQueryData = {
	comments?: {
		nodes?: ReadonlyArray<LegacyCommentQueryNode | null | undefined> | null;
	} | null;
};

type UnifiedCommentLocation = CommentLocation | LegacyCommentQueryLocation;

type RendererAnnotationMark = { attrs?: { id?: string } };

interface WindowWithIdleScheduling extends Window {
	requestIdleCallback(callback: IdleRequestCallback, options?: IdleRequestOptions): number;
	cancelIdleCallback(handle: number): void;
}

// ---------------------------------------------------------------------------
// Shared type
// ---------------------------------------------------------------------------
/** Both Comment (data-manager) and GraphQL nodes have an author field */
type CommentWithAuthor = Pick<CommentData, 'author'> & {
	author?: {
		displayName?: string | null;
		accountId?: string | null;
	} | null;
};

// ---------------------------------------------------------------------------
// Unified GraphQL query (superset of both individual queries)
// Fetches inline fields + footer fields in a single request.
// ---------------------------------------------------------------------------
const CommentQueryParamsListenerQuery = graphql`
	query CommentQueryParamsListenerQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				parentId
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							resolved
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
						}
					}
				}
				ancestors {
					id
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								resolved
							}
						}
					}
				}
			}
		}
	}
`;

// ---------------------------------------------------------------------------
// Constants (from ObjectCommentQueryParamsListener)
// ---------------------------------------------------------------------------
const SCROLL_TIMEOUT_MS = 4000;
const PAGE_STABILITY_DELAY_MS = 500;
const HEIGHT_STABILITY_CHECK_MS = 150;
const expectedErrorCodes = [404];

export const isExpectedCommentQueryLegacyError = (error: ApolloErrorInterop) => {
	const list = error.graphQLErrors;
	if (!Array.isArray(list) || list.length === 0) {
		return false;
	}
	return list.some((e: { extensions?: { statusCode?: unknown; code?: unknown } }) => {
		const code = e?.extensions?.code;
		if (code === 'NOT_FOUND') {
			return true;
		}
		const statusCode = e?.extensions?.statusCode;
		if (statusCode == null) {
			return false;
		}
		const n = Number(statusCode);
		return Number.isFinite(n) && expectedErrorCodes.includes(n);
	});
};

/** Legacy interop error handling matched InlineCommentQueryParamsListener vs ObjectCommentQueryParamsListener */
type LegacyCommentQueryInteropErrorMode = 'inline' | 'object';

function resolveLegacyCommentQueryInteropErrorMode(
	isNewFlow: boolean,
	foundComment: Comment | CommentReply | null | undefined,
	legacyData: LegacyCommentQueryData | null | undefined,
	legacyError: ApolloErrorInterop | null | undefined,
): LegacyCommentQueryInteropErrorMode {
	if (isNewFlow) {
		const t = (foundComment as { location?: { type?: string } | null } | null | undefined)?.location
			?.type;
		return t && t !== 'INLINE' ? 'object' : 'inline';
	}
	const node = legacyData?.comments?.nodes?.[0];
	const t = node?.location?.type;
	if (t && t !== 'INLINE') {
		return 'object';
	}
	// Error with no comment node: location unknown — use object semantics for expected 404s only
	if (legacyError && !node) {
		return isExpectedCommentQueryLegacyError(legacyError) ? 'object' : 'inline';
	}
	return 'inline';
}

function handleLegacyCommentQueryInteropError(
	legacyError: ApolloErrorInterop | null | undefined,
	mode: LegacyCommentQueryInteropErrorMode,
): void {
	if (!legacyError) {
		return;
	}
	if (mode === 'inline') {
		// Missing/deleted comment (404 / NOT_FOUND) is expected — do not mark as handled (matches object-listener).
		if (!isExpectedCommentQueryLegacyError(legacyError)) {
			logger.error`An error occurred when querying the focused comment status; ${legacyError}`;
			markErrorAsHandled(legacyError);
		}
		return;
	}
	// ObjectCommentQueryParamsListener: log unexpected only; never markErrorAsHandled
	if (!isExpectedCommentQueryLegacyError(legacyError)) {
		logger.error`An error occurred when querying the focused object comment status; ${legacyError}`;
	}
}

// ---------------------------------------------------------------------------
// i18n (from InlineCommentQueryParamsListener)
// ---------------------------------------------------------------------------
export const i18n = defineMessages({
	commentLinkNotFoundFlagTitle: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.title',
		defaultMessage: 'This comment is not available',
		description: 'Title for a warning flag when the user clicked a link to a deleted comment.',
	},
	commentLinkNotFoundFlagDescription: {
		id: 'comments-query-param-handlers.comment-link-not-found.flag.description',
		defaultMessage: 'It may be deleted.',
		description: 'Warning message for a link to a deleted comment.',
	},
});

// ---------------------------------------------------------------------------
// Query param hooks
// ---------------------------------------------------------------------------
const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

const logger = getLogger('inline-comments:query-param-listener');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export type CommentQueryParamsListenerProps = {
	// Inline-specific
	isArchived?: boolean;
	rendererRef?: React.RefObject<Element>;
	// Object-specific
	hasLoadedObjectComments?: boolean;
	allObjectCommentsRendered?: boolean;
	commentBatchRendered?: boolean;
	commentBatchCount?: number;
	setForceLoadComments?: (value: React.SetStateAction<boolean>) => void;
};

// ---------------------------------------------------------------------------
// useCommentQueryParamsData — shared data fetching hook
// Handles both new flow (useCommentById) and old flow (GraphQL query).
// Returns unified comment data regardless of which flow is active.
// ---------------------------------------------------------------------------
type CommentQueryParamsData = {
	commentId: string | null | undefined;
	// New flow
	foundComment: Comment | CommentReply | null | undefined;
	parentComment: Comment | CommentReply | null | undefined;
	commentLoading: boolean;
	commentError: boolean;
	parentLoading: boolean;
	parentError: boolean;
	// Old flow
	legacyData: LegacyCommentQueryData | null | undefined;
	legacyError: ApolloErrorInterop | null | undefined;
	legacyLoading: boolean;
	// Derived
	isResolved: boolean | undefined;
	parentId: string | null | undefined;
	rawCommentForReply: Comment | CommentReply | LegacyCommentQueryNode | null | undefined;
};

function useCommentQueryParamsData({
	commentId,
	contentId,
}: {
	commentId: string | null | undefined;
	contentId: string | null | undefined;
}): CommentQueryParamsData {
	const pageMode = useGetPageMode();
	const contentStatusFilters = buildDefaultCommentsContentStatusFilters(pageMode);

	// NEW FLOW
	const {
		comment: foundComment,
		loading: commentLoading,
		error: commentError,
	} = useCommentById({
		commentId: commentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip: !commentId || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		},
	});

	const {
		comment: parentComment,
		loading: parentLoading,
		error: parentError,
	} = useCommentById({
		commentId: foundComment?.parentId || '',
		contentId: contentId || '',
		contentStatus: contentStatusFilters,
		options: {
			skip:
				!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) ||
				!foundComment?.parentId,
		},
	});

	// OLD FLOW — unified query fetches superset of both inline + footer fields
	const {
		data: legacyData,
		loading: legacyLoading,
		error: legacyError,
	} = useQueryInterop<LegacyCommentQueryData, { commentId: string }>(
		CommentQueryParamsListenerQuery,
		{
			variables: { commentId: commentId! },
			skip: !commentId || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		},
	);

	const legacyInteropErrorMode = resolveLegacyCommentQueryInteropErrorMode(
		expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		foundComment,
		legacyData,
		legacyError,
	);
	handleLegacyCommentQueryInteropError(legacyError, legacyInteropErrorMode);

	// Derive isResolved + parentId from whichever flow is active
	let isResolved: boolean | undefined = undefined;
	let parentId: string | null | undefined = undefined;

	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		if (foundComment && !commentError) {
			const loc = foundComment.location;
			if (loc?.type === 'INLINE') {
				isResolved = Boolean(loc?.inlineResolveProperties?.resolved);
			} else {
				isResolved = Boolean(loc?.commentResolveProperties?.resolved);
			}
			parentId = foundComment.parentId;
		}
	} else {
		if (legacyData && !legacyError) {
			const node = legacyData?.comments?.nodes?.[0];
			const loc = node?.location;
			if (loc?.type === 'INLINE') {
				isResolved = Boolean(loc?.inlineResolveProperties?.resolved);
			} else {
				isResolved = Boolean(loc?.commentResolveProperties?.resolved);
			}
			parentId = node?.parentId;
		}
	}

	const rawCommentForReply = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? foundComment
		: (legacyData?.comments?.nodes?.[0] ?? null);

	return {
		commentId,
		foundComment,
		parentComment,
		commentLoading,
		commentError: Boolean(commentError),
		parentLoading,
		parentError: Boolean(parentError),
		legacyData,
		legacyError,
		legacyLoading,
		isResolved,
		parentId,
		rawCommentForReply,
	};
}

// ---------------------------------------------------------------------------
// InlineCommentHandler — all inline-specific logic extracted from
// InlineCommentQueryParamsListener
// ---------------------------------------------------------------------------
type InlineCommentHandlerProps = {
	commentId: string | null | undefined;
	isArchived: boolean;
	rendererRef: React.RefObject<Element>;
	flags: WithFlagsProps['flags'];
	data: CommentQueryParamsData;
};

const InlineCommentHandler: React.FC<InlineCommentHandlerProps> = ({
	commentId,
	isArchived,
	rendererRef,
	flags,
	data,
}) => {
	const hasHandledCommentLink = useRef(false);
	const { showCommentsPanel } = useShowCommentsPanel();
	const { orderedActiveAnnotationIdList } = useCommentsDataState();
	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus, setReplyToCommentId } =
		useCommentsPanelActions();
	const { rendererActions } = useRendererActions();

	const { replyToCommentId, isCommentNotFound } = useInlineCommentQueryParams({
		queryArchivedContent: isArchived,
	});

	const { foundComment, isResolved: _ignored, parentComment } = data;

	const queryParamData = useMemo(() => {
		if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
			if (!data.commentError && !data.commentLoading && foundComment) {
				const location: UnifiedCommentLocation | undefined = foundComment.location ?? undefined;
				const pid = foundComment.parentId;
				const parentLocation: UnifiedCommentLocation | undefined =
					pid && !data.parentError && !data.parentLoading && parentComment
						? (parentComment.location ?? undefined)
						: undefined;
				if (!pid || parentComment) {
					return { location, parentId: pid, parentLocation };
				}
			}
			return undefined;
		} else {
			if (!data.legacyError && !data.legacyLoading && data.legacyData) {
				const node = data.legacyData?.comments?.nodes?.[0];
				const location: UnifiedCommentLocation | undefined = node?.location ?? undefined;
				const pid = node?.parentId;
				const parentLocation: UnifiedCommentLocation | undefined =
					node?.ancestors?.[0]?.location ?? undefined;
				return { location, parentId: pid, parentLocation };
			}
			return undefined;
		}
	}, [
		data.commentError,
		data.commentLoading,
		foundComment,
		data.parentError,
		data.parentLoading,
		parentComment,
		data.legacyData,
		data.legacyError,
		data.legacyLoading,
	]);

	const queryParamDataExt = useMemo(() => {
		const { location: loc, parentLocation: pLoc } = queryParamData || {};
		return loc?.inlineMarkerRef
			? {
					annotationId: loc.inlineMarkerRef,
					isResolved: Boolean(loc.inlineResolveProperties?.resolved),
				}
			: {
					annotationId: pLoc?.inlineMarkerRef,
					isResolved: Boolean(pLoc?.inlineResolveProperties?.resolved),
				};
	}, [queryParamData]);

	const onReplyToCommentReady = useCallback(() => {
		if (!replyToCommentId) return;
		const rawComment = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? foundComment
			: data.legacyData?.comments?.nodes?.[0];
		if (rawComment) {
			const annotationId = queryParamDataExt?.annotationId;
			if (annotationId) {
				setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
			}
			const author = (rawComment as CommentWithAuthor)?.author;
			setReplyToCommentId(
				createReplyToCommentParams(
					replyToCommentId,
					LocationValue.PANEL,
					author as AuthorWithAccountId | null | undefined,
					fg('confluence_frontend_content_wrapper'),
				),
			);
		}
	}, [
		replyToCommentId,
		foundComment,
		data.legacyData,
		queryParamDataExt,
		setReplyToCommentId,
		setCurrentlySelectedThreadKey,
	]);

	const tryToFocusAndScrollActiveCommentCallback = useCallback(() => {
		return tryToFocusAndScrollActiveComment({
			parentId: queryParamData?.parentId,
			annotationId: queryParamDataExt?.annotationId || null,
			commentId: commentId || null,
		});
	}, [queryParamData, queryParamDataExt, commentId]);

	useRetryOnMutation({
		callback: tryToFocusAndScrollActiveCommentCallback,
		elementRef: rendererRef,
		skip: !commentId || !queryParamData || queryParamDataExt.isResolved,
	});

	useEffect(() => {
		if (!queryParamData || hasHandledCommentLink.current) return;
		const { location } = queryParamData;
		const { annotationId } = queryParamDataExt;

		if (location) {
			if (queryParamDataExt.isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				if (annotationId) {
					setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
					setCommentContainerToFocus(commentId ?? undefined);
				}
			} else if (annotationId && fg('confluence_frontend_new_dangling_comments_ux')) {
				// Comment exists in DB but annotation mark may be gone from page content (deleted content).
				// Check if annotation mark exists in renderer; if not, open DELETED_CONTENT panel.
				const annotationExists = rendererActions
					?.getAnnotationMarks()
					?.some((mark: RendererAnnotationMark) => mark.attrs?.id === annotationId);
				if (!annotationExists) {
					setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
					setCommentContainerToFocus(commentId ?? undefined);
					showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
					expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true) &&
						onReplyToCommentReady();
					hasHandledCommentLink.current = true;
					return;
				}
			}
		} else {
			if (expValEquals('confluence_comment_improve_comment_deeplink', 'isEnabled', true)) {
				if (!rendererActions || (annotationId && isCommentNotFound === undefined)) {
					return;
				}
			}
			if (
				!rendererActions &&
				expValEquals('confluence_comment_improve_comment_deeplink', 'isEnabled', true)
			) {
				return;
			}
			const annotationExists = rendererActions
				?.getAnnotationMarks()
				?.some((mark: RendererAnnotationMark) => mark.attrs?.id === annotationId);

			if (annotationId && !annotationExists && fg('confluence_frontend_new_dangling_comments_ux')) {
				setCurrentlySelectedThreadKey(annotationId, CommentType.INLINE);
				setCommentContainerToFocus(commentId ?? undefined);
				showCommentsPanel({ openWithView: ViewValues.DELETED_CONTENT });
				expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true) &&
					onReplyToCommentReady();
			}
			hasHandledCommentLink.current = true;
		}
	}, [
		queryParamData,
		rendererActions,
		orderedActiveAnnotationIdList,
		queryParamDataExt,
		showCommentsPanel,
		setCurrentlySelectedThreadKey,
		setCommentContainerToFocus,
		commentId,
		isCommentNotFound,
		onReplyToCommentReady,
	]);

	useEffect(() => {
		if (isCommentNotFound) {
			void flags.showWarningFlag({
				id: `viewpage-comment-link-not-found-${commentId}`,
				title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
				description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
			});
		}
	}, [commentId, flags, isCommentNotFound]);

	return null;
};

// Helper (kept private — only used by InlineCommentHandler)
const tryToFocusAndScrollActiveComment = ({
	parentId,
	annotationId,
	commentId,
}: {
	parentId: string | null | undefined;
	annotationId: string | null;
	commentId: string | null;
}): boolean => {
	const mark = document.querySelector<HTMLElement>(`[data-id="${annotationId}"]`);
	if (mark) {
		const focusedMarkerRef = document.querySelector("[data-has-focus='true']");
		if (focusedMarkerRef?.id !== annotationId) {
			const emitter = getRendererAnnotationEventEmitter();
			checkElementExpand(mark, true);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [annotationId],
				eventTarget: mark,
			});
		}
		if (commentId) {
			const success = handleDeepLinkNavigation({
				scrollBehavior: ScrollBehavior.INSTANT,
				context: NavigationContext.RENDERER,
				block: 'center',
				markElement: mark,
				commentId,
			});
			if (!success) {
				abortAll('new_interaction', 'focused-comment-scroll');
				scrollToElementWithViewingRoom({ element: mark, useInstantScroll: true });
			}
		} else if (!parentId) {
			abortAll('new_interaction', 'focused-comment-scroll');
			scrollToElementWithViewingRoom({ element: mark, useInstantScroll: true });
		}
	}
	return !!mark;
};

// ---------------------------------------------------------------------------
// ObjectCommentHandler — all footer/general comment logic extracted from
// ObjectCommentQueryParamsListener
// ---------------------------------------------------------------------------
type ObjectCommentHandlerProps = {
	commentId: string | null | undefined;
	focusedCommentId: string | null | undefined;
	replyToComment: string | null | undefined;
	hasLoadedObjectComments: boolean;
	allObjectCommentsRendered: boolean;
	commentBatchRendered?: boolean;
	commentBatchCount?: number;
	setForceLoadComments: (value: React.SetStateAction<boolean>) => void;
	data: CommentQueryParamsData;
};

const ObjectCommentHandler: React.FC<ObjectCommentHandlerProps> = ({
	commentId,
	focusedCommentId,
	replyToComment,
	hasLoadedObjectComments,
	allObjectCommentsRendered,
	commentBatchRendered,
	commentBatchCount,
	setForceLoadComments,
	data,
}) => {
	const [hasScrolledToFocusedComment, setHasScrolledToFocusedComment] = useState<boolean>(false);
	const [hasScrolledToReplyComment, setHasScrolledToReplyComment] = useState<boolean>(false);
	const mutationObserverRef = useRef<MutationObserver | null>(null);
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | number | null>(null);
	const lastMutationTimeRef = useRef<number>(0);

	const clearScrollTimeout = useCallback(() => {
		if (scrollTimeoutRef.current !== null) {
			if (typeof scrollTimeoutRef.current === 'number') {
				if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
					(window as unknown as WindowWithIdleScheduling).cancelIdleCallback(
						scrollTimeoutRef.current as number,
					);
				}
			} else {
				clearTimeout(scrollTimeoutRef.current);
			}
			scrollTimeoutRef.current = null;
		}
	}, []);

	const cleanupMutationObserver = useCallback(() => {
		if (mutationObserverRef.current) {
			mutationObserverRef.current.disconnect();
			mutationObserverRef.current = null;
		}
	}, []);

	const cleanupScrollResources = useCallback(() => {
		if (mutationObserverRef.current) cleanupMutationObserver();
		if (scrollTimeoutRef.current !== null) clearScrollTimeout();
	}, [cleanupMutationObserver, clearScrollTimeout]);

	const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();
	const processedCommentsData = useProcessedCommentsDataState();
	const { commentsDataMap } = useCommentsDataState();

	const { isResolved, parentId, rawCommentForReply, foundComment, legacyData, commentError } = data;

	// Update currentlySelectedThreadKey to root comment for replies (after scroll)
	useEffect(() => {
		const hasData = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
			? foundComment && !commentError
			: legacyData && !data.legacyError;
		if (
			parentId &&
			(hasScrolledToFocusedComment || hasScrolledToReplyComment) &&
			expValEquals('confluence_comment_deeplink_thread_selection', 'isEnabled', true)
		) {
			let rootCommentId: string | undefined;
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				if (processedCommentsData.isDataProcessed && processedCommentsData.allComments.length > 0) {
					rootCommentId = findRootCommentForReply(processedCommentsData.allComments, parentId)?.id;
				}
			} else {
				const legacyFooterComments = Object.values(commentsDataMap.general);
				if (legacyFooterComments.length > 0) {
					rootCommentId = findRootCommentForReply(
						legacyFooterComments as unknown as Comment[],
						parentId,
					)?.id;
				}
			}
			if (rootCommentId && rootCommentId !== parentId) {
				setCurrentlySelectedThreadKey(rootCommentId);
			}
		}

		// Early return if this is actually an INLINE comment — handled by InlineCommentHandler
		if (
			hasData &&
			commentId &&
			expValEquals('confluence_comment_deeplink_queryparam_selection', 'isEnabled', true)
		) {
			const loc = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
				? foundComment?.location
				: legacyData?.comments?.nodes?.[0]?.location;
			if (loc && 'type' in loc && loc.type === 'INLINE') return;
		}

		if (
			commentId &&
			hasData &&
			hasLoadedObjectComments &&
			allObjectCommentsRendered &&
			!hasScrolledToFocusedComment &&
			!hasScrolledToReplyComment
		) {
			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				setCurrentlySelectedThreadKey(commentId, CommentType.GENERAL);
				if (focusedCommentId) {
					setHasScrolledToFocusedComment(true);
				} else {
					setHasScrolledToReplyComment(true);
				}
			} else {
				const commentElementId = `footer-comment-thread-${commentId}-container`;
				const replyCommentElementId = `footer-comment-thread-${commentId}-replies`;

				let threadToBeSelected = commentId;
				if (
					parentId &&
					expValEquals('confluence_comment_deeplink_thread_selection', 'isEnabled', true)
				) {
					let rootCommentId: string | undefined;
					if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
						if (
							processedCommentsData.isDataProcessed &&
							processedCommentsData.allComments.length > 0
						) {
							rootCommentId = findRootCommentForReply(
								processedCommentsData.allComments,
								parentId,
							)?.id;
						}
					} else {
						const legacyFooterComments = Object.values(commentsDataMap.general);
						if (legacyFooterComments.length > 0) {
							rootCommentId = findRootCommentForReply(
								legacyFooterComments as unknown as Comment[],
								parentId,
							)?.id;
						}
					}
					threadToBeSelected = rootCommentId ?? parentId;
				}

				if (commentId) {
					if (expValEquals('confluence_comment_deeplink_delay_scroll', 'isEnabled', true)) {
						if (replyToComment) {
							setCurrentlySelectedThreadKey(threadToBeSelected);
							const author = (rawCommentForReply as unknown as CommentWithAuthor)?.author;
							setReplyToCommentId(
								createReplyToCommentParams(
									replyToComment,
									LocationValue.FOOTER,
									author as AuthorWithAccountId | null | undefined,
									expValEquals('confluence_comment_expand_reply_editor', 'isEnabled', true),
								),
							);
							setHasScrolledToReplyComment(true);
							return;
						}

						cleanupMutationObserver();

						const getDocumentScrollHeight = () =>
							Math.max(
								document.documentElement?.scrollHeight ?? 0,
								document.body?.scrollHeight ?? 0,
							);

						const scrollToComment = () => {
							const el =
								document.getElementById(commentElementId) ||
								document.getElementById(replyCommentElementId);
							if (el && fg('confluence_comment_footer_scrolling_fix')) {
								void el.offsetTop;
								void getDocumentScrollHeight();
							}
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.INSTANT,
								block: 'center',
								inline: 'center',
								commentId,
								navigationContext: parentId ? NavigationContext.REPLY : undefined,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(threadToBeSelected);
							cleanupMutationObserver();
						};

						const performScrollAndCleanup = () => {
							clearScrollTimeout();
							if (fg('confluence_comment_footer_scrolling_fix')) {
								requestAnimationFrame(() => requestAnimationFrame(scrollToComment));
							} else {
								scrollToComment();
							}
						};

						const checkHeightStabilityThenScroll = () => {
							if (!fg('confluence_comment_footer_scrolling_fix')) {
								performScrollAndCleanup();
								return;
							}
							const h1 = getDocumentScrollHeight();
							scrollTimeoutRef.current = setTimeout(() => {
								scrollTimeoutRef.current = null;
								const h2 = getDocumentScrollHeight();
								if (h1 === h2) {
									performScrollAndCleanup();
								} else {
									scheduleScroll();
								}
							}, HEIGHT_STABILITY_CHECK_MS);
						};

						const scheduleScroll = () => {
							clearScrollTimeout();
							lastMutationTimeRef.current = Date.now();
							if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
								scrollTimeoutRef.current = (
									window as unknown as WindowWithIdleScheduling
								).requestIdleCallback(() => {
									const checkStabilityAndScroll = () => {
										const timeSince = Date.now() - lastMutationTimeRef.current;
										if (timeSince >= PAGE_STABILITY_DELAY_MS) {
											checkHeightStabilityThenScroll();
										} else {
											scrollTimeoutRef.current = setTimeout(() => {
												scrollTimeoutRef.current = null;
												checkStabilityAndScroll();
											}, PAGE_STABILITY_DELAY_MS - timeSince);
										}
									};
									checkStabilityAndScroll();
								});
							} else {
								scrollTimeoutRef.current = setTimeout(() => {
									scrollTimeoutRef.current = null;
									checkHeightStabilityThenScroll();
								}, SCROLL_TIMEOUT_MS);
							}
						};

						const getElementAndCheckReady = () => {
							const el =
								document.getElementById(commentElementId) ||
								document.getElementById(replyCommentElementId);
							if (!el) return { element: null, isReady: false };
							const rect = el.getBoundingClientRect();
							return {
								element: el,
								isReady:
									el.offsetHeight > 0 && el.offsetWidth > 0 && rect.height > 0 && rect.width > 0,
							};
						};

						const { element: currentElement, isReady } = getElementAndCheckReady();
						if (currentElement && isReady) {
							scheduleScroll();
						} else {
							mutationObserverRef.current = new MutationObserver(() => {
								lastMutationTimeRef.current = Date.now();
								const { element: el, isReady: elReady } = getElementAndCheckReady();
								if (el && elReady) scheduleScroll();
							});
							if (currentElement) {
								mutationObserverRef.current.observe(currentElement, {
									childList: true,
									subtree: true,
									attributes: true,
									attributeFilter: ['style', 'class'],
								});
								const parent = currentElement.parentElement;
								if (parent)
									mutationObserverRef.current.observe(parent, { childList: true, subtree: true });
							} else {
								mutationObserverRef.current.observe(document.body, {
									childList: true,
									subtree: true,
								});
							}
							setTimeout(() => {
								if (mutationObserverRef.current) checkHeightStabilityThenScroll();
							}, SCROLL_TIMEOUT_MS);
						}
					} else {
						const el =
							document.getElementById(commentElementId) ||
							document.getElementById(replyCommentElementId);
						if (el) {
							handleFooterCommentsNavigation({
								scrollBehavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
								commentId,
								navigationContext: parentId ? NavigationContext.REPLY : undefined,
							});
							setHasScrolledToFocusedComment(true);
							setCurrentlySelectedThreadKey(threadToBeSelected);
						}
					}
				}
			}
		}
	}, [
		legacyData,
		data.legacyError,
		foundComment,
		commentError,
		hasLoadedObjectComments,
		commentId,
		focusedCommentId,
		replyToComment,
		allObjectCommentsRendered,
		commentBatchRendered,
		commentBatchCount,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
		isResolved,
		showCommentsPanel,
		hasScrolledToFocusedComment,
		hasScrolledToReplyComment,
		rawCommentForReply,
		parentId,
		processedCommentsData,
		commentsDataMap,
		cleanupScrollResources,
		cleanupMutationObserver,
		clearScrollTimeout,
	]);

	useEffect(() => {
		setHasScrolledToFocusedComment(false);
		setHasScrolledToReplyComment(false);
		cleanupScrollResources();
	}, [focusedCommentId, replyToComment, cleanupScrollResources]);

	useEffect(() => () => cleanupScrollResources(), [cleanupScrollResources]);

	useEffect(() => {
		if ((focusedCommentId || replyToComment) && !hasLoadedObjectComments) {
			setForceLoadComments(true);
		}
	}, [focusedCommentId, replyToComment, hasLoadedObjectComments, setForceLoadComments]);

	return null;
};

// ---------------------------------------------------------------------------
// CommentQueryParamsListenerComponent — root unified component
//
// Fetches data once and routes to InlineCommentHandler or ObjectCommentHandler
// based on the comment's location.type.
// ---------------------------------------------------------------------------
export const CommentQueryParamsListenerComponent: React.FC<
	CommentQueryParamsListenerProps & WithFlagsProps
> = ({
	isArchived = false,
	rendererRef,
	hasLoadedObjectComments = false,
	allObjectCommentsRendered = false,
	commentBatchRendered,
	commentBatchCount,
	setForceLoadComments,
	flags,
}) => {
	const [contentId] = usePageContentId();

	// Inline query params (used for InlineCommentHandler + shared commentId)
	const {
		focusedCommentId: inlineFocusedId,
		replyToCommentId,
		editCommentId,
	} = useInlineCommentQueryParams({ queryArchivedContent: isArchived });

	// Object query params
	const focusedCommentIdQP = useFocusedCommentIdQueryParam();
	const replyToCommentQP = useReplyToCommentQueryParam();

	// Unified commentId: prefer inline params, fall back to object params
	const commentId =
		inlineFocusedId || replyToCommentId || editCommentId || focusedCommentIdQP || replyToCommentQP;

	// Single shared data fetch — one query for all comment types
	const sharedData = useCommentQueryParamsData({ commentId, contentId });

	// Determine comment type from data (new or old flow)
	const commentLocationType = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? sharedData.foundComment?.location?.type
		: sharedData.legacyData?.comments?.nodes?.[0]?.location?.type;

	// Treat undefined (not yet loaded) as INLINE to satisfy InlineCommentHandler
	// which handles the "not found" case. Once loaded, route based on actual type.
	const isInlineComment = !commentLocationType || commentLocationType === 'INLINE';

	if (isInlineComment && rendererRef) {
		return (
			<InlineCommentHandler
				commentId={commentId}
				isArchived={isArchived}
				rendererRef={rendererRef}
				flags={flags}
				data={sharedData}
			/>
		);
	}

	if (!isInlineComment && setForceLoadComments) {
		return (
			<ObjectCommentHandler
				commentId={commentId}
				focusedCommentId={focusedCommentIdQP || inlineFocusedId}
				replyToComment={replyToCommentQP || replyToCommentId}
				hasLoadedObjectComments={hasLoadedObjectComments}
				allObjectCommentsRendered={allObjectCommentsRendered}
				commentBatchRendered={commentBatchRendered}
				commentBatchCount={commentBatchCount}
				setForceLoadComments={setForceLoadComments}
				data={sharedData}
			/>
		);
	}

	return null;
};

// ---------------------------------------------------------------------------
// Public export — wrapped with flags HOC (same pattern as existing components)
// ---------------------------------------------------------------------------
export const CommentQueryParamsListener = withFlags(CommentQueryParamsListenerComponent);
